// src/components/SubmissionsTable/ColumnManagerDrawer.jsx
import React, {
  useMemo,
} from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ColumnDraftManager from '../FormSettings/FormSubmitSettings/ColumnDraftManager';
import { useFormSubmissionSettings } from '../../contexts/FormSubmissionSettingsContext';

// Provider-owned column registry.
//
// Enterprise integration note:
// - ColumnManagerDrawer should not know Bitly, HubSpot, Slack, BigQuery, etc.
// - It only asks the provider registry for provider-owned columns when a
//   provider already exists in the active/fallback column model.
// - This keeps Manage Columns scalable as more integrations add provider output
//   and provider metric columns.
import {
  listProviderColumnDefinitions,
} from '../../integrations/providerColumns';
import {
  getProviderColumnSpec,
  normalizeColumnOrder,
  upsertProviderColumns,
} from '../../utils/providerOutputColumnUtils';

// (Optional) per-user roaming persistence — only used if persistPerUser === true.
// These helpers already exist in your utils/firestore.js.
import { saveColumnOrderToFirestore } from '../../utils/firestore';
import { useFormContext } from '../../contexts/FormContext';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Extract provider ids that already appear in a column model.
 *
 * Enterprise rule:
 * - Do not list every possible provider in Manage Columns.
 * - Only list provider-owned columns for providers already enabled/configured
 *   on this form/table surface.
 */
function getProviderIdsFromColumnOrder(columnOrder = []) {
  const normalized = normalizeColumnOrder(columnOrder);

  return Array.from(
    new Set(
      normalized
        .map((column) => getProviderColumnSpec(column))
        .filter((spec) => spec.isProviderColumn && spec.provider)
        .map((spec) => spec.provider)
    )
  );
}

/**
 * Builds provider -> sourceUfi hints from existing provider output columns.
 *
 * Example:
 * - Bitly Short URL can carry sourceUfi so the provider definition remains
 *   attached to the source URL field that produced the short link.
 *
 * This is generic: any provider can carry sourceUfi on its output columns.
 */
function getSourceUfiByProvider(columnOrder = []) {
  const normalized = normalizeColumnOrder(columnOrder);

  return normalized.reduce((acc, column) => {
    const spec = getProviderColumnSpec(column);

    if (!spec.isProviderColumn || !spec.provider) {
      return acc;
    }

    if (!acc[spec.provider] && column.sourceUfi) {
      acc[spec.provider] = column.sourceUfi;
    }

    return acc;
  }, {});
}

function getProviderColumns(columnOrder = []) {
  return normalizeColumnOrder(columnOrder).filter((column) => {
    const spec = getProviderColumnSpec(column);
    return spec.isProviderColumn;
  });
}

/**
 * Builds the column model shown in Manage Columns.
 *
 * Why this exists:
 * - The Submissions Page has its own column bucket: submissionsPageTable.
 * - The in-form Display Table has another bucket: addToSubmissionsTable.
 * - Once submissionsPageTable exists, it no longer fully falls back to
 *   addToSubmissionsTable.
 * - New provider metric columns, such as Bitly Clicks, can therefore be
 *   missing from Manage Columns unless the drawer merges provider registry
 *   definitions into the active model.
 *
 * Enterprise behavior:
 * - Start with the active surface bucket when it exists.
 * - Fall back to addToSubmissionsTable for first-time page use.
 * - Carry over provider-owned columns from the fallback bucket when the page
 *   bucket already exists but is missing newly registered provider columns.
 * - Ask the provider registry for provider-owned output/metric definitions.
 * - Append missing provider definitions as hidden columns unless they already
 *   exist and have a user-selected visibility/order.
 */
function buildManageableColumnOrder({
  activeColumnOrder = [],
  fallbackColumnOrder = [],
}) {
  const active = normalizeColumnOrder(activeColumnOrder);
  const fallback = normalizeColumnOrder(fallbackColumnOrder);

  let merged = active.length ? active : fallback;

  const sourceColumns = [
    ...fallback,
    ...active,
  ];

  const providerIds = getProviderIdsFromColumnOrder(sourceColumns);

  if (!providerIds.length) {
    return normalizeColumnOrder(merged);
  }

  const sourceUfiByProvider = {
    ...getSourceUfiByProvider(fallback),
    ...getSourceUfiByProvider(active),
  };

  const fallbackProviderColumns = getProviderColumns(fallback);

  if (active.length && fallbackProviderColumns.length) {
    merged = upsertProviderColumns(
      merged,
      fallbackProviderColumns.map((column, index) => ({
        ...column,
        order: Number.isFinite(column.order)
          ? column.order
          : merged.length + index,
      })),
      {
        preserveExistingOrder: true,
        preserveExistingVisibility: true,
      }
    );
  }

  const providerDefinitions = listProviderColumnDefinitions({
    providerIds,
    includeMetrics: true,
    includeExperimentalMetrics: false,
    sourceUfiByProvider,
  });

  if (!providerDefinitions.length) {
    return normalizeColumnOrder(merged);
  }

  const providerDefinitionsForAppend = providerDefinitions.map(
    (column, index) => ({
      ...column,
      // Provider builders may default order to 0. For missing columns, append
      // them to the existing model instead of inserting them at the front.
      order: merged.length + index,
    })
  );

  return upsertProviderColumns(
    merged,
    providerDefinitionsForAppend,
    {
      preserveExistingOrder: true,
      preserveExistingVisibility: true,
    }
  );
}

/**
 * ColumnManagerDrawer (enterprise split: surface-specific configuration)
 * --------------------------------------------------------------------
 * PURPOSE
 *   This drawer is the UI for managing the column model used by either:
 *     • The in-form DisplayTable experience  → "addToSubmissionsTable"
 *     • The dedicated Submissions page       → "submissionsPageTable"
 *
 *   By separating buckets, product/design can tune the in-form experience
 *   independently from the operations/reporting view on the Submissions page.
 *
 * BEHAVIOR
 *   • Reads from the page-specific bucket FIRST when it exists.
 *   • Falls back to 'addToSubmissionsTable' automatically on first run.
 *   • Merges provider-owned output/metric definitions for configured
 *     providers so newly added integration columns appear in Manage Columns.
 *   • Writes ONLY to the active bucket (does not touch the other).
 *   • Optional: per-user persistence to Firestore via saveColumnOrderToFirestore.
 *
 * COMPATIBILITY
 *   • Does NOT alter your existing in-form setup or contexts.
 *   • If you continue to render the in-form DisplayTable using the
 *     addToSubmissionsTable settings (as you do today), nothing breaks.
 *
 * PROPS
 *   open               – boolean; drawer visibility
 *   onClose            – function; close handler
 *   scope              – "submissionsPage" | "preview"
 *                          "submissionsPage" → use 'submissionsPageTable'
 *                          "preview"         → use 'addToSubmissionsTable'
 *                        Defaults to "submissionsPage".
 *   persistPerUser     – boolean; when true, also calls saveColumnOrderToFirestore(formId, uid, order)
 *                        Defaults to false so existing behavior is unchanged.
 */
export default function ColumnManagerDrawer({
  open,
  onClose,
  scope = 'submissionsPage',     // default to submissions page manager
  persistPerUser = false,        // off by default; enable if you want roaming prefs per user
}) {
  const {
    submissionSettings,
    updateTableSurfaceSettings,
  } = useFormSubmissionSettings();
  const { formId } = useFormContext() || {};
  const { currentUser } = useAuth() || {};

  /* Decide which settings bucket to use for this surface.
     - In-form "preview"   → 'addToSubmissionsTable' (matches your existing setup)
     - Submissions page    → 'submissionsPageTable'
     We only WRITE to the chosen bucket. For reading we fall back to the in-form bucket if empty. */
  const settingsKey =
    scope === 'submissionsPage' ? 'submissionsPageTable' : 'addToSubmissionsTable';

  const activeColumnOrder =
    submissionSettings?.[settingsKey]?.columnOrder || [];

  const fallbackColumnOrder =
    submissionSettings?.addToSubmissionsTable?.columnOrder || [];

  /* Read current columnOrder:
     1) Try the surface-specific bucket
     2) Fall back to the in-form bucket so first-time use still shows columns
     3) Merge provider-owned output/metric definitions for configured providers
     4) Else empty array */
  const columnOrder = useMemo(() => {
    const nextColumnOrder = buildManageableColumnOrder({
      activeColumnOrder,
      fallbackColumnOrder,
    });

    try {
      console.log('[ColumnManagerDrawer] Built manageable column order:', {
        scope,
        settingsKey,
        activeCount: activeColumnOrder.length,
        fallbackCount: fallbackColumnOrder.length,
        managedCount: nextColumnOrder.length,
        providerColumnIds: nextColumnOrder
          .filter((column) => getProviderColumnSpec(column).isProviderColumn)
          .map((column) => column.id),
      });
    } catch {
      // ignore logging issues
    }

    return nextColumnOrder;
  }, [
    activeColumnOrder,
    fallbackColumnOrder,
    scope,
    settingsKey,
  ]);

  // Friendly title for the header.
  const title =
    scope === 'submissionsPage'
      ? 'Manage Columns (Submissions Page)'
      : 'Manage Columns (In-Form DisplayTable)';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 360 } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: '1px solid #eee',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body: ColumnDraftManager owns the full list (real + virtual + calc).
          We pass the current columnOrder and persist ONLY to this surface's bucket. */}
      <Box sx={{ p: 1 }}>
        <ColumnDraftManager
          columnOrder={columnOrder}
          settingsKey={settingsKey}
          onChange={async (updatedOrder) => {
            // 1) Persist to the page-specific bucket so each surface keeps its own view.
            //    This DOES NOT touch the other bucket and does NOT toggle submit actions.
            updateTableSurfaceSettings(settingsKey, { columnOrder: updatedOrder });

            // 2) Diagnostics: keep a light, safe log
            try {
              const ids = (updatedOrder || []).map((c) =>
                typeof c === 'string' ? c : c?.id
              );
              console.log(
                `[ColumnManagerDrawer] Saved updated columnOrder to '${settingsKey}'`,
                { ids }
              );
            } catch {
              // ignore logging issues
            }

            // 3) (Optional) Per-user roaming persistence across devices/browsers
            //    Only runs if persistPerUser === true and we have formId + user id.
            if (persistPerUser) {
              try {
                if (formId && currentUser?.uid) {
                  await saveColumnOrderToFirestore(formId, currentUser.uid, updatedOrder);
                  console.log(
                    `[ColumnManagerDrawer] Per-user columnOrder persisted (form=${formId}, user=${currentUser.uid})`
                  );
                } else {
                  console.warn(
                    '[ColumnManagerDrawer] persistPerUser enabled but formId or user missing; skipping Firestore persistence.'
                  );
                }
              } catch (e) {
                console.warn(
                  '[ColumnManagerDrawer] saveColumnOrderToFirestore failed:',
                  e?.message || e
                );
              }
            }
          }}
        />
      </Box>
    </Drawer>
  );
}
